import React, { useState } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import ACLStore from "lib/aclStore";

import classes from "./ProductUpdatesV2ItemStyles.scss";
import classNames from "classnames";
import { getStringAutoLinks, getStyleStrippedText } from "Utils";
import { Button, Tooltip } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import BetaRequestModal from "../BetaRequestModal/BetaRequestModal";
import { TickDoubleOutlined, TickCircleFilled } from "@toddle-edu/ds-icons";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import { openLinkInNewTab } from "modules/Services";
import {
  getModuleIcon,
  getIsAllowBetaFeatureRequestEnabled,
  getFeatureAvailableForRequest,
} from "../../modules/ProductUpdatesV2Utils";

import { TODDLE_2_O_PERMISSION } from "Constants/permissionConstants";

const getHighlightedText = ({ text, highlight }) => {
  if (!text || !highlight) return text;

  // Escape special characters in the highlight term
  const escapedHighlight = highlight.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");

  // Split on highlight term and include term into parts, ignore case
  const parts = _.split(text, new RegExp(`(${escapedHighlight})`, "gi"));

  return (
    <span>
      {" "}
      {_.map(parts, (part, i) => (
        <span
          key={`highlightText-${i}`}
          className={
            _.lowerCase(part) === _.lowerCase(highlight)
              ? classNames(classes.highlightedTextColor, "text-label-l")
              : {}
          }
        >
          {part}
        </span>
      ))}{" "}
    </span>
  );
};

const ProductUpdatesV2Item = props => {
  const { t } = useI18n(["common"]);
  const {
    productUpdateItem = {},
    testId,
    productUpdateId,
    productUpdates,
    searchText = "",
    openLinkInNewTab,
    sourceFrom = "",
    isToddleSupportOrDevUser,
  } = props;
  const { releaseItems } = productUpdateItem;
  const { module, version, isBeta, betaRequestStatus } = _.first(releaseItems);
  const showBetaRequest = _.some(releaseItems, releaseItem =>
    _.get(releaseItem, "isBetaRequestAllowed")
  );
  const isNewModule = _.get(module, "isNewModule");
  const isAdmin = getIsAdminByRole();

  const [showBetaRequestModal, setShowBetaRequestModal] = useState(false);
  const isModal = sourceFrom === "modal";

  const onClickRequestButton = () => {
    setShowBetaRequestModal(true);
  };

  const isFeatureAvailableForRequest = getFeatureAvailableForRequest({
    releaseItems,
  });

  const renderButtonComponent = () => {
    switch (betaRequestStatus) {
      case "REQUESTED":
        return (
          <Tooltip
            tooltip={t(
              isFeatureAvailableForRequest
                ? "common:already_requested"
                : "common:the_request_for_beta_version_is_already_sent"
            )}
            placement={"bottom"}
          >
            <Button
              variant="neutral"
              type="outlined"
              size={"medium"}
              disabled={true}
              dsVersion="2.0"
              icon={<TickDoubleOutlined variant={"success"} />}
              testId={`${testId}-beta-requested`}
            >
              {t(
                isFeatureAvailableForRequest
                  ? "common:request_submitted"
                  : "common:requested_for_beta"
              )}
            </Button>
          </Tooltip>
        );
      case "DONE":
        return (
          <Tooltip
            tooltip={t(
              isFeatureAvailableForRequest
                ? "common:feature_enabled_for_your_school"
                : "common:beta_version_of_this_module_is_enabled_for_your_school"
            )}
            placement={"bottom"}
          >
            <Button
              variant="neutral"
              type="outlined"
              size={"medium"}
              dsVersion="2.0"
              icon={<TickCircleFilled variant={"success"} />}
              testId={`${testId}-beta-enabled`}
            >
              <div className={classes.buttonText}>
                {t(
                  isFeatureAvailableForRequest
                    ? "common:feature_enabled_for_your_school"
                    : "common:beta_version_enabled_for_your_school"
                )}
              </div>
            </Button>
          </Tooltip>
        );
      default:
        return (
          <Button
            variant="neutral"
            type="outlined"
            size={"medium"}
            dsVersion="2.0"
            onClick={onClickRequestButton}
            testId={`${testId}-beta-request`}
          >
            {t(
              isFeatureAvailableForRequest
                ? "common:request_for_the_feature"
                : "common:request_for_beta"
            )}
          </Button>
        );
    }
  };

  const { icon, color } = getModuleIcon({
    moduleKey: _.get(module, "moduleKey"),
  });

  const isToddle2Enabled = ACLStore.can(TODDLE_2_O_PERMISSION);

  const mainContainerClass = classNames({
    [classes.mainContainer]: true,
    [classes.productUpdateItemGap]: isModal,
  });

  const getIsBetaRequestButtonEnabled = () => {
    return (
      showBetaRequest &&
      isAdmin &&
      !isToddleSupportOrDevUser &&
      getIsAllowBetaFeatureRequestEnabled()
    );
  };

  const showBetaRequestButton = getIsBetaRequestButtonEnabled();

  const showVersionBetaLabel = () => {
    if (!version && !isBeta) {
      return null;
    }

    const betaFeatureLabel = isFeatureAvailableForRequest
      ? t("common:available_on_request")
      : t("common:beta");

    const betaLabel = isFeatureAvailableForRequest
      ? betaFeatureLabel // keep the same casing
      : _.lowerCase(betaFeatureLabel); // this will make '(Beta)' to '(beta)' for version label

    let versionText = "";
    if (version && isBeta) {
      versionText = `${version} (${betaLabel})`;
    } else if (version) {
      versionText = version;
    } else if (isBeta) {
      versionText = betaFeatureLabel; // keep the same casing for beta label if no version is there
    }

    return (
      <div className={classNames("text-body", classes.moduleName)}>
        {versionText}
      </div>
    );
  };

  return (
    <div className={mainContainerClass}>
      <div className={classes.headerContainer}>
        <div className={classes.itemTagsContainer}>
          {icon ? (
            <div
              className={classes.iconStyle}
              style={{ backgroundColor: color }}
            >
              {icon}
            </div>
          ) : null}
          <div className={classes.moduleContainer}>
            <div className={classes.itemTagsContainer}>
              <div className={"text-label-l"}>
                {getHighlightedText({
                  text: _.get(module, "label"),
                  highlight: searchText,
                })}
              </div>
              {isNewModule ? (
                <div className={classNames(classes.newContainer, "text-body")}>
                  {t("common:new")}
                </div>
              ) : null}
            </div>
            {showVersionBetaLabel()}
          </div>
        </div>
        {showBetaRequestButton ? (
          <div className={classes.requestButton}>{renderButtonComponent()}</div>
        ) : null}
      </div>
      {_.map(releaseItems, releaseItem => {
        const { description, title, categories, redirectUrl } = releaseItem;
        const updatedDescription = getStringAutoLinks(description);
        const programCategories = _.sortBy(
          _.filter(
            categories,
            categoryItem =>
              _.get(categoryItem, "group") === "Curriculum" &&
              _.get(categoryItem, "type") !== "TODDLE_PLAY"
          ),
          "displaySequence"
        );

        const onClickRedirectIcon = () => {
          if (redirectUrl) {
            openLinkInNewTab({
              url: redirectUrl,
            });
          }
        };

        const iconContainerClasses = classNames({
          [classes.iconContainer]: true,
          [classes.redirectionContainer]: redirectUrl,
        });

        return (
          <div className={classes.itemTitleContainer}>
            <div className={iconContainerClasses} onClick={onClickRedirectIcon}>
              <div className="text-label">
                {getHighlightedText({
                  text: title,
                  highlight: searchText,
                })}
              </div>
            </div>
            <div
              className={classNames("text-body", classes.description)}
              dangerouslySetInnerHTML={{
                __html: getStyleStrippedText({
                  text: updatedDescription || "",
                  type: "SANITIZE_DANGER_HTML_PARAGRAPH",
                }),
              }}
            />
            {!isToddle2Enabled ? (
              <div className={classes.categoryContainer}>
                {_.map(programCategories, (categoryItem, index) => {
                  const categoryItemTitle =
                    _.get(categoryItem, "type") === "UBD"
                      ? "Independent curricula"
                      : _.get(categoryItem, "title");
                  return (
                    <>
                      <div
                        className={classNames(
                          "text-body-s",
                          classes.categoryItem
                        )}
                      >
                        {categoryItemTitle}
                      </div>
                      {index !== _.size(programCategories) - 1 ? (
                        <div className={classes.dotContainer}>
                          <div className={classes.dot}></div>
                        </div>
                      ) : null}
                    </>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
      {showBetaRequestModal ? (
        <BetaRequestModal
          testId={testId}
          setShowBetaRequestModal={setShowBetaRequestModal}
          groupedProductUpdateItems={[productUpdateItem]}
          productUpdateId={productUpdateId}
          productUpdates={productUpdates}
        />
      ) : null}
    </div>
  );
};

const mapActionCreators = {
  openLinkInNewTab,
};

export default connect(null, mapActionCreators)(ProductUpdatesV2Item);
